import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Brain, Sparkle, CheckCircle, Warning, Lightbulb, ArrowsClockwise,
  CircleNotch, Clock,
} from "@phosphor-icons/react";

/**
 * KnowledgeDiagnostic — "fator uau" iter-71.7.
 *
 * Renders the AI-generated diagnostic of the tenant's Cérebro. Three
 * visual states:
 *   1. Loading the latest summary (cold)        — slim skeleton
 *   2. No summary yet                            — empty CTA card
 *   3. Has summary                               — rich diagnostic card
 *
 * Generation is rate-limited at 30/calendar-month per tenant by the
 * backend. We expose the quota live ("23/30 este mês") so the operator
 * knows how many they can still spend.
 *
 * Props:
 *   onToast(msg, level?) — wired to the parent toast helper.
 *   currentDocCount       — live count of ACTIVE non-expired docs the
 *                           parent currently has loaded. Compared
 *                           against `summary.doc_count` (snapshot)
 *                           to flag the diagnostic as stale.
 */
function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Math.max(0, Date.now() - t);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "agora mesmo";
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h}h`;
  const d = Math.floor(h / 24);
  return `há ${d}d`;
}

export default function KnowledgeDiagnostic({
  onToast,
  currentDocCount,
  onUseRecommendation,
}) {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [summary, setSummary] = useState(null);
  const [quota, setQuota] = useState({ used: 0, limit: 30, remaining: 30 });
  // Iter-71.8 — separate quota for "Use this recommendation" drafts.
  // 60/month — drafts are lighter LLM calls (~3k tokens).
  const [draftQuota, setDraftQuota] = useState({
    used: 0, limit: 60, remaining: 60,
  });
  // Index of the recommendation currently being turned into a draft —
  // used to show a per-row spinner without blocking the rest of the UI.
  const [draftingIdx, setDraftingIdx] = useState(null);

  const fetchLatest = async () => {
    setLoading(true);
    try {
      const [latestRes, draftQuotaRes] = await Promise.all([
        api.get("/knowledge/summary/latest"),
        api.get("/knowledge/summary/draft/quota").catch(() => ({ data: null })),
      ]);
      setSummary(latestRes.data?.summary || null);
      setQuota(latestRes.data?.quota || { used: 0, limit: 30, remaining: 30 });
      if (draftQuotaRes.data) setDraftQuota(draftQuotaRes.data);
    } catch (_e) {
      // Non-fatal — silently leave the panel in its empty state. The
      // operator can still try to generate.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLatest(); }, []);

  /**
   * Iter-71.8 — Click a recommendation → ask Claude for a draft →
   * open the parent editor pre-filled. The parent's `onUseRecommendation`
   * handler is responsible for opening the editor and focusing it.
   * Per-row spinner so the operator gets feedback on which one fired.
   *
   * Named `handleRecommendation` (not `useRecommendation`) on purpose
   * — ESLint enforces React's "hooks-only-at-top-level" rule for any
   * identifier starting with `use*`, which would block this nested
   * inside an `onClick={() => …}` callback.
   */
  const handleRecommendation = async (text, idx) => {
    if (draftingIdx !== null) return;  // one at a time
    if (!onUseRecommendation) return;
    if (draftQuota.remaining <= 0) {
      onToast?.("Limite mensal de rascunhos atingido.", "error");
      return;
    }
    setDraftingIdx(idx);
    try {
      const res = await api.post("/knowledge/summary/draft", {
        recommendation: text,
      });
      setDraftQuota(res.data?.quota || draftQuota);
      onUseRecommendation(res.data?.draft || {});
    } catch (e) {
      const detail = e?.response?.data?.detail || "Falha ao gerar rascunho.";
      onToast?.(detail, "error");
    } finally {
      setDraftingIdx(null);
    }
  };

  const generate = async () => {
    if (generating || quota.remaining <= 0) return;
    setGenerating(true);
    try {
      const res = await api.post("/knowledge/summary");
      setSummary(res.data?.summary || null);
      setQuota(res.data?.quota || quota);
      // Refetch draft quota too — they're independent but the operator
      // might check both badges after a single action.
      try {
        const dq = await api.get("/knowledge/summary/draft/quota");
        if (dq.data) setDraftQuota(dq.data);
      } catch (_e) { /* non-fatal */ }
      onToast?.("Diagnóstico do Cérebro gerado.");
    } catch (e) {
      const detail = e?.response?.data?.detail || "Falha ao gerar diagnóstico.";
      onToast?.(detail, "error");
    } finally {
      setGenerating(false);
    }
  };

  // Diagnostic is "stale" if the current doc inventory differs from
  // the snapshot the summary was generated against. Comparing
  // `summary.doc_count` (snapshot stored on the persisted summary)
  // against the live `currentDocCount` prop is fully deterministic
  // and survives remounts — earlier versions used an in-memory
  // monotonic counter that could miss the bump in race conditions
  // between the parent's `setItems` + `bumpVersion` batched render.
  const stale = !!(
    summary &&
    typeof currentDocCount === "number" &&
    typeof summary.doc_count === "number" &&
    currentDocCount !== summary.doc_count
  );

  if (loading) {
    return (
      <div className="rounded-2xl border border-slate-200/70 bg-gradient-to-br from-violet-50/40 via-white to-white px-5 py-4 flex items-center gap-3">
        <CircleNotch size={14} className="animate-spin text-violet-600" />
        <span className="text-[12.5px] text-slate-500">Carregando diagnóstico…</span>
      </div>
    );
  }

  const quotaExhausted = quota.remaining <= 0;
  const quotaBadge = (
    <span
      data-testid="kb-summary-quota"
      className={`text-[10.5px] uppercase tracking-[0.06em] font-semibold px-2 py-0.5 rounded-full border ${
        quotaExhausted
          ? "bg-rose-50 text-rose-700 border-rose-200"
          : quota.remaining <= 5
          ? "bg-amber-50 text-amber-700 border-amber-200"
          : "bg-slate-50 text-slate-500 border-slate-200"
      }`}
      title={`Limite mensal: ${quota.used}/${quota.limit} usados — renova no dia 1º`}
    >
      {quota.used}/{quota.limit} este mês
    </span>
  );

  // ── EMPTY STATE — no summary generated yet ──────────────────────
  if (!summary) {
    return (
      <div
        data-testid="kb-summary-empty"
        className="rounded-2xl border border-slate-200/70 bg-gradient-to-br from-violet-50/50 via-white to-white overflow-hidden"
      >
        <div className="px-5 py-4 flex items-start gap-4">
          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-violet-600 to-violet-800 text-white flex items-center justify-center shrink-0 shadow-sm">
            <Brain size={16} weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="text-[13.5px] font-semibold text-slate-900 tracking-tight leading-tight">
                Diagnóstico do Cérebro
              </h4>
              {quotaBadge}
            </div>
            <p className="text-[12px] text-slate-500 leading-relaxed mt-1.5 max-w-[58ch]">
              Receba em 1 clique um diagnóstico do que seu Cérebro cobre,
              o que tá faltando e o que vale adicionar agora. O Agentus lê
              tudo que você cadastrou e devolve um plano de ação.
            </p>
            <button
              type="button"
              onClick={generate}
              disabled={generating || quotaExhausted}
              data-testid="kb-summary-generate-empty"
              className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-[12.5px] font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {generating ? (
                <>
                  <CircleNotch size={12} className="animate-spin" />
                  Analisando seu Cérebro…
                </>
              ) : quotaExhausted ? (
                "Limite mensal atingido"
              ) : (
                <>
                  <Sparkle size={12} weight="fill" />
                  Gerar diagnóstico
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── HAS-SUMMARY STATE ───────────────────────────────────────────
  return (
    <div
      data-testid="kb-summary-card"
      className="rounded-2xl border border-slate-200/70 bg-gradient-to-br from-violet-50/40 via-white to-white overflow-hidden"
    >
      <div className="px-5 py-4 border-b border-slate-100/80 flex items-start gap-3.5">
        <div className="h-8 w-8 rounded-xl bg-gradient-to-br from-violet-600 to-violet-800 text-white flex items-center justify-center shrink-0 shadow-sm">
          <Brain size={14} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-[13.5px] font-semibold text-slate-900 tracking-tight leading-tight">
              Diagnóstico do Cérebro
            </h4>
            <span
              className="text-[10.5px] text-slate-400 inline-flex items-center gap-1"
              title={`Análise baseada em ${summary.doc_count} doc(s) ativos`}
            >
              <Clock size={10} /> {timeAgo(summary.created_at)} · {summary.doc_count} doc(s)
            </span>
            {stale && (
              <span
                data-testid="kb-summary-stale"
                className="text-[10px] uppercase tracking-[0.06em] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
                title="Você editou o Cérebro depois desse diagnóstico — vale regenerar."
              >
                Pode estar desatualizado
              </span>
            )}
            {quotaBadge}
          </div>
          <p
            data-testid="kb-summary-overview"
            className="text-[13px] text-slate-700 leading-relaxed mt-2"
          >
            {summary.overview}
          </p>
        </div>
        <button
          type="button"
          onClick={generate}
          disabled={generating || quotaExhausted}
          data-testid="kb-summary-regenerate"
          title={quotaExhausted ? "Limite mensal atingido — renova no dia 1º" : "Gerar um novo diagnóstico"}
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-slate-200 hover:border-slate-900 text-slate-700 text-[11.5px] font-medium disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          {generating ? (
            <CircleNotch size={11} className="animate-spin" />
          ) : (
            <ArrowsClockwise size={11} weight="bold" />
          )}
          {generating ? "Gerando…" : "Regenerar"}
        </button>
      </div>

      <div className="px-5 py-4 grid grid-cols-1 lg:grid-cols-3 gap-x-6 gap-y-4">
        {summary.strengths?.length > 0 && (
          <div data-testid="kb-summary-strengths">
            <div className="text-[10px] uppercase tracking-[0.08em] font-semibold text-emerald-700 flex items-center gap-1 mb-2">
              <CheckCircle size={10} weight="fill" /> Pontos fortes
            </div>
            <ul className="space-y-1.5">
              {summary.strengths.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-[12px] text-slate-700 leading-snug">
                  <span className="mt-[7px] h-[3px] w-[3px] rounded-full bg-emerald-600 shrink-0" />
                  <span>{s}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {summary.gaps?.length > 0 && (
          <div data-testid="kb-summary-gaps">
            <div className="text-[10px] uppercase tracking-[0.08em] font-semibold text-amber-700 flex items-center gap-1 mb-2">
              <Warning size={10} weight="fill" /> Lacunas detectadas
            </div>
            <ul className="space-y-1.5">
              {summary.gaps.map((g, i) => (
                <li key={i} className="flex items-start gap-2 text-[12px] text-slate-700 leading-snug">
                  <span className="mt-[7px] h-[3px] w-[3px] rounded-full bg-amber-600 shrink-0" />
                  <span>{g}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {summary.recommendations?.length > 0 && (
          <div data-testid="kb-summary-recommendations">
            <div className="text-[10px] uppercase tracking-[0.08em] font-semibold text-violet-700 flex items-center gap-1 mb-2">
              <Lightbulb size={10} weight="fill" /> Próximas ações
              {onUseRecommendation && draftQuota.remaining < draftQuota.limit && (
                <span
                  data-testid="kb-draft-quota"
                  className="ml-1 normal-case tracking-normal text-[10px] font-medium text-slate-400"
                  title={`Rascunhos via IA: ${draftQuota.used}/${draftQuota.limit} este mês`}
                >
                  · {draftQuota.remaining}/{draftQuota.limit} rascunhos
                </span>
              )}
            </div>
            <ul className="space-y-1">
              {summary.recommendations.map((r, i) => {
                const isDrafting = draftingIdx === i;
                const exhausted = draftQuota.remaining <= 0;
                // When the parent doesn't wire onUseRecommendation,
                // degrade gracefully to plain text rows.
                if (!onUseRecommendation) {
                  return (
                    <li key={i} className="flex items-start gap-2 text-[12px] text-slate-700 leading-snug py-1">
                      <span className="mt-[7px] h-[3px] w-[3px] rounded-full bg-violet-600 shrink-0" />
                      <span>{r}</span>
                    </li>
                  );
                }
                return (
                  <li key={i}>
                    <button
                      type="button"
                      onClick={() => handleRecommendation(r, i)}
                      disabled={draftingIdx !== null || exhausted}
                      data-testid={`kb-rec-use-${i}`}
                      title={
                        exhausted
                          ? "Limite mensal de rascunhos atingido"
                          : "Clique pra criar um doc novo a partir desta sugestão"
                      }
                      className="w-full text-left flex items-start gap-2 px-2 py-1.5 -mx-2 rounded-lg text-[12px] text-slate-700 leading-snug transition-colors hover:bg-violet-50/70 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-transparent group"
                    >
                      <span className="mt-[7px] h-[3px] w-[3px] rounded-full bg-violet-600 shrink-0" />
                      <span className="flex-1">{r}</span>
                      <span className="text-[10px] text-violet-700 font-medium opacity-0 group-hover:opacity-100 group-disabled:opacity-0 transition-opacity shrink-0 self-center inline-flex items-center gap-1">
                        {isDrafting ? (
                          <>
                            <CircleNotch size={10} className="animate-spin" />
                            Gerando…
                          </>
                        ) : (
                          <>
                            <Sparkle size={9} weight="fill" />
                            Usar
                          </>
                        )}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
